/**
 * Electron Bridge Utility
 *
 * Typed wrapper around window.electronAPI to avoid direct inline
 * property access throughout the codebase. All Electron IPC calls
 * should go through these functions so TypeScript can verify the
 * call sites and the global declaration lives in one place.
 */

// ============================================================================
// Global type declaration
// ============================================================================

export interface ExportVideoFileResult {
  success: boolean;
  filePath?: string;
  canceled?: boolean;
  error?: string;
}

export interface SaveMediaFileResult {
  success: boolean;
  filePath?: string;
  canceled?: boolean;
  error?: string;
}

/**
 * Discriminated result for native picker dialogs (selectFolder / selectVideoFile).
 *
 * - `{ status: 'selected'; path: string }` — user picked a path
 * - `{ status: 'cancelled' }` — user dismissed the dialog (not an error)
 * - `{ status: 'unavailable' }` — not running inside Electron
 * - `{ status: 'error'; error: string }` — IPC call threw
 *
 * Callers MUST distinguish 'error' from 'cancelled'/'unavailable' and surface
 * a message to the user when status === 'error'.
 */
export type PickerResult =
  | { status: 'selected'; path: string }
  | { status: 'cancelled' }
  | { status: 'unavailable' }
  | { status: 'error'; error: string };

interface ElectronBridgeAPI {
  isElectron?: boolean;
  selectFolder?: () => Promise<string | null>;
  selectVideoFile?: () => Promise<string | null>;
  getDownloadsPath?: () => Promise<string>;
  openVideoFolder?: (filePath: string) => Promise<boolean>;
  exportVideoFile?: (
    sourcePath: string,
    suggestedFilename?: string
  ) => Promise<ExportVideoFileResult>;
  saveMediaFile?: (dataUrl: string, suggestedFilename?: string) => Promise<SaveMediaFileResult>;
  onOpenSettings?: (callback: () => void) => () => void;
  sendLog?: (level: 'error' | 'warn' | 'info', message: string) => void;
}

declare global {
  interface Window {
    electronAPI?: ElectronBridgeAPI;
  }
}

// ============================================================================
// Bridge functions
// ============================================================================

/**
 * Open a native folder picker dialog.
 *
 * Returns a discriminated PickerResult so callers can distinguish between
 * a real IPC error (status 'error'), a user cancel (status 'cancelled'),
 * and "not running in Electron" (status 'unavailable').
 */
export async function selectFolder(): Promise<PickerResult> {
  if (!window.electronAPI?.selectFolder) {
    return { status: 'unavailable' };
  }
  try {
    const result = await window.electronAPI.selectFolder();
    if (result === null) return { status: 'cancelled' };
    return { status: 'selected', path: result };
  } catch (err) {
    return { status: 'error', error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Open a native file picker filtered to video files.
 *
 * Returns a discriminated PickerResult so callers can distinguish between
 * a real IPC error (status 'error'), a user cancel (status 'cancelled'),
 * and "not running in Electron" (status 'unavailable').
 */
export async function selectVideoFile(): Promise<PickerResult> {
  if (!window.electronAPI?.selectVideoFile) {
    return { status: 'unavailable' };
  }
  try {
    const result = await window.electronAPI.selectVideoFile();
    if (result === null) return { status: 'cancelled' };
    return { status: 'selected', path: result };
  } catch (err) {
    return { status: 'error', error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Get the real OS Downloads folder path (honors Windows Known Folder
 * redirection, e.g. Downloads relocated to OneDrive). Returns null when not
 * running inside Electron or when the IPC call fails.
 */
export async function getDownloadsPath(): Promise<string | null> {
  if (!window.electronAPI?.getDownloadsPath) {
    return null;
  }
  try {
    return await window.electronAPI.getDownloadsPath();
  } catch {
    return null;
  }
}

/**
 * Open the system file manager with the given file selected.
 * Only available when running inside the Electron desktop app.
 *
 * @returns true if the folder was opened successfully, false otherwise.
 */
export async function openVideoFolder(filePath: string): Promise<boolean> {
  if (!window.electronAPI?.openVideoFolder) {
    return false;
  }
  try {
    return await window.electronAPI.openVideoFolder(filePath);
  } catch {
    return false;
  }
}

/**
 * Export a generated video by copying it to a user-chosen destination via a
 * native save dialog. Returns a structured result (success / canceled / error).
 * Only available when running inside the Electron desktop app.
 */
export async function exportVideoFile(
  sourcePath: string,
  suggestedFilename?: string
): Promise<ExportVideoFileResult> {
  if (!window.electronAPI?.exportVideoFile) {
    return { success: false, error: 'Not running in Electron' };
  }
  try {
    return await window.electronAPI.exportVideoFile(sourcePath, suggestedFilename);
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Save a generated media file (a `data:` URL or raw base64 string) to disk via
 * a native save dialog and reveal it in the file manager. Returns a structured
 * result (success / canceled / error). Only available inside the desktop app.
 */
export async function saveMediaFile(
  dataUrl: string,
  suggestedFilename?: string
): Promise<SaveMediaFileResult> {
  if (!window.electronAPI?.saveMediaFile) {
    return { success: false, error: 'Not running in Electron' };
  }
  try {
    return await window.electronAPI.saveMediaFile(dataUrl, suggestedFilename);
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Register a callback that fires when the user selects
 * Preferences from the Electron application menu.
 *
 * @returns a cleanup function that removes the listener.
 */
export function onOpenSettings(callback: () => void): () => void {
  if (!window.electronAPI?.onOpenSettings) {
    return () => {
      // no-op: not running in Electron
    };
  }
  return window.electronAPI.onOpenSettings(callback);
}

/**
 * Check whether the app is running inside the Electron shell.
 */
export function isElectronAvailable(): boolean {
  return window.electronAPI?.isElectron === true;
}
